import { useCallback, useEffect, useRef, useState } from 'react';
import { getViolationRuleActivation } from '../services/violations';

/**
 * FE-FIX07. В списке правил не было видно, действует правило всегда или по
 * расписанию. Batch-endpoint'а для активации нет, поэтому грузим её только
 * для правил текущей страницы.
 *
 * Пул на 4 параллельных запроса: без него страница на 100 правил открывает
 * сотню соединений разом. Кэш по rule_id переживает пагинацию назад-вперёд.
 * Упавший запрос остаётся прочерком — ошибка одного правила не имеет права
 * ронять весь список.
 */
const MODE_LABELS = {
  always: 'Всегда',
  scheduled: 'По расписанию',
};

const CONCURRENCY = 4;

const runPool = (ids, worker) => {
  let cursor = 0;
  const next = () => {
    if (cursor >= ids.length) return Promise.resolve();
    const id = ids[cursor];
    cursor += 1;
    return worker(id).then(next);
  };
  return Promise.all(Array.from({ length: Math.min(CONCURRENCY, ids.length) }, next));
};

export const useRuleActivationModes = (ruleIds, { refreshToken = 0 } = {}) => {
  const cacheRef = useRef(new Map());
  const [, forceRender] = useState(0);
  const key = (Array.isArray(ruleIds) ? ruleIds : []).join(',');

  useEffect(() => { cacheRef.current = new Map(); forceRender((n) => n + 1); }, [refreshToken]);

  useEffect(() => {
    const ids = key ? key.split(',') : [];
    const pending = ids.filter((id) => id && !cacheRef.current.has(id));
    if (pending.length === 0) return undefined;

    let cancelled = false;
    pending.forEach((id) => cacheRef.current.set(id, { state: 'loading' }));

    runPool(pending, (id) =>
      getViolationRuleActivation(id)
        .then((activation) => { cacheRef.current.set(id, { state: 'ready', mode: activation?.mode }); })
        .catch(() => { cacheRef.current.set(id, { state: 'error' }); })
    ).then(() => { if (!cancelled) forceRender((n) => n + 1); });

    return () => { cancelled = true; };
  }, [key, refreshToken]);

  const modeLabel = useCallback((ruleId) => {
    const entry = cacheRef.current.get(ruleId);
    if (entry?.state !== 'ready') return '—';
    return MODE_LABELS[entry.mode] ?? '—';
  }, []);

  const failed = useCallback(
    (ruleId) => cacheRef.current.get(ruleId)?.state === 'error',
    []
  );

  return { modeLabel, failed };
};

export default useRuleActivationModes;
